import { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { useTranslation } from 'react-i18next';
import { Button, Icon, EmptyState } from '@/design-system';
import { motion, AnimatePresence } from 'framer-motion';

export type ScannerMode = 'auto' | 'photo';

interface UnifiedScannerProps {
  /** When 'auto' (default), zxing decodes barcode/QR continuously. When 'photo', the camera streams without decoding so the user can capture a label image. */
  mode?: ScannerMode;
  continuous?: boolean;
  onCode: (code: string) => void;
  onPhoto: (file: File) => void;
}

/**
 * Continuous barcode/QR decoder using @zxing/browser, with a label-photo
 * capture path. Mode is controlled by the parent so the surrounding tabs can
 * switch between deterministic decoding and AI label scanning.
 */
export function UnifiedScanner({ mode = 'auto', continuous = false, onCode, onPhoto }: UnifiedScannerProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastCodeRef = useRef<{ code: string; at: number } | null>(null);
  const [active, setActive] = useState(false);
  const [permission, setPermission] = useState<'idle' | 'granted' | 'denied'>('idle');
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setActive(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setActive(true);
    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        throw new Error(t('scan.cameraSecureContextBody'));
      }
      const video = videoRef.current;
      if (!video) throw new Error('Camera view is not ready.');

      if (mode === 'auto') {
        readerRef.current = new BrowserMultiFormatReader();
        const controls = await readerRef.current.decodeFromVideoDevice(
          undefined,
          video,
          (result) => {
            if (result) {
              const code = result.getText();
              if (continuous) {
                const now = Date.now();
                const last = lastCodeRef.current;
                if (last?.code === code && now - last.at < 1800) return;
                lastCodeRef.current = { code, at: now };
                onCode(code);
                return;
              }
              controls.stop();
              setActive(false);
              onCode(code);
            }
          },
        );
        controlsRef.current = controls;
      } else {
        // Photo mode: just stream, no decoder.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        streamRef.current = stream;
        video.srcObject = stream;
        await video.play().catch(() => undefined);
      }
      setPermission('granted');
    } catch (e) {
      setActive(false);
      setPermission('denied');
      setError(e instanceof Error ? e.message : 'Camera error');
    }
  }, [continuous, mode, onCode, t]);

  // If mode changes while the camera is running, restart cleanly.
  useEffect(() => {
    if (active) {
      stop();
      // Defer restart so video element resets first.
      const id = setTimeout(() => void start(), 30);
      return () => clearTimeout(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Cleanup on unmount.
  useEffect(() => () => stop(), [stop]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `label-${Date.now()}.jpg`, { type: 'image/jpeg' });
          onPhoto(file);
          stop();
        }
      },
      'image/jpeg',
      0.92,
    );
  }, [onPhoto, stop]);

  const placeholderText = mode === 'photo' ? t('scan.awaitingPhoto') : t('scan.awaitingCode');

  return (
    <div className="grid gap-4">
      {permission === 'denied' && (
        <EmptyState
          icon="warning"
          title={t('scan.cameraPermissionTitle')}
          description={error ?? t('scan.cameraPermissionBody')}
          action={
            <Button onClick={start} iconLeft="camera">
              {t('scan.cameraStart')}
            </Button>
          }
        />
      )}

      <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-line bg-ink-soft">
        <video
          ref={videoRef}
          className={active ? 'h-full w-full object-cover' : 'hidden'}
          playsInline
          muted
        />
        <AnimatePresence>
          {!active && (
            <motion.div
              key="placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 grid place-items-center text-center"
            >
              <div className="grid gap-3">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-candle/10 text-candle">
                  <Icon name="camera" size={26} />
                </div>
                <p className="text-sm text-parchment max-w-xs">{placeholderText}</p>
                <Button onClick={start} iconLeft="camera">
                  {t('scan.cameraStart')}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* corner reticle (shape varies by mode) */}
        {active && (
          <div className="pointer-events-none absolute inset-6">
            <div className="absolute left-0 top-0 h-8 w-8 rounded-tl-md border-l-2 border-t-2 border-candle" />
            <div className="absolute right-0 top-0 h-8 w-8 rounded-tr-md border-r-2 border-t-2 border-candle" />
            <div className="absolute bottom-0 left-0 h-8 w-8 rounded-bl-md border-b-2 border-l-2 border-candle" />
            <div className="absolute bottom-0 right-0 h-8 w-8 rounded-br-md border-b-2 border-r-2 border-candle" />
            {mode === 'auto' && (
              <motion.div
                className="absolute left-0 right-0 h-px bg-candle/60"
                animate={{ top: ['20%', '80%', '20%'] }}
                transition={{ duration: 2.6, ease: 'easeInOut', repeat: Infinity }}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {active && (
            <Button variant="ghost" onClick={stop} iconLeft="x">
              {t('scan.cameraStop')}
            </Button>
          )}
          {active && mode === 'photo' && (
            <Button variant="primary" onClick={capture} iconLeft="camera">
              {t('scan.capture')}
            </Button>
          )}
        </div>
        <label className="ui-btn ui-btn--ghost cursor-pointer">
          <Icon name="upload" size={16} />
          {t('scan.upload')}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onPhoto(file);
            }}
          />
        </label>
      </div>
    </div>
  );
}
